/**
 * CmsIcon — renders any Lucide or Tabler icon by name from the CMS.
 * Icons are mapped statically to enable Next.js tree-shaking.
 *
 * ─────────────────────────────────────────────────────────────
 * CMS icon field format
 * ─────────────────────────────────────────────────────────────
 *  Lucide (default):  "wifi"  /  "bed-double"  /  "shield-check"
 *  Lucide (prefix):   "lucide:wifi"
 *  Tabler (prefix):   "tabler:pool"  /  "tabler:brand-whatsapp"
 * ─────────────────────────────────────────────────────────────
 */

import { 
  Sparkles, Wifi, BedDouble, ShieldCheck, MapPin, Phone, Mail, Clock, 
  Instagram, Star, ChevronLeft, ChevronRight, ChevronDown, Wrench, 
  CalendarX, User, Navigation, X, CheckCircle2, Coffee, Waves, Tv,
  Car, Utensils, Bath, Key, Check, Info, AlertTriangle, Monitor,
  type LucideIcon 
} from 'lucide-react';
import { 
  IconBrandWhatsapp, IconBrandFacebook, IconBrandInstagram, 
  IconBrandTwitter, IconBrandYoutube, IconPool, IconYoga, IconChefHat,
  IconMassage, IconTrees,
  type Icon as TablerIcon
} from '@tabler/icons-react';

const LUCIDE_MAP: Record<string, LucideIcon> = {
  'wifi': Wifi,
  'bed-double': BedDouble,
  'shield-check': ShieldCheck,
  'map-pin': MapPin,
  'phone': Phone,
  'mail': Mail,
  'clock': Clock,
  'instagram': Instagram,
  'star': Star,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-down': ChevronDown,
  'wrench': Wrench,
  'calendar-x': CalendarX,
  'user': User,
  'navigation': Navigation,
  'x': X,
  'check-circle-2': CheckCircle2,
  'coffee': Coffee,
  'waves': Waves,
  'tv': Tv,
  'car': Car,
  'utensils': Utensils,
  'bath': Bath,
  'sparkles': Sparkles,
  'key': Key,
  'check': Check,
  'info': Info,
  'alert-triangle': AlertTriangle,
  'monitor': Monitor
};

const TABLER_MAP: Record<string, TablerIcon> = {
  'brand-whatsapp': IconBrandWhatsapp,
  'brand-facebook': IconBrandFacebook,
  'brand-instagram': IconBrandInstagram,
  'brand-twitter': IconBrandTwitter,
  'brand-youtube': IconBrandYoutube,
  'pool': IconPool,
  'yoga': IconYoga,
  'chef-hat': IconChefHat,
  'massage': IconMassage,
  'trees': IconTrees
};

/** Parse "tabler:wifi" → { set, name }. Bare name → lucide. */
function parse(raw: string | null | undefined): { set: string; name: string } {
  if (!raw) return { set: 'lucide', name: '' };
  const trimmed = raw.trim();
  const colon   = trimmed.indexOf(':');
  if (colon === -1) return { set: 'lucide', name: trimmed };
  const set  = trimmed.slice(0, colon).toLowerCase();
  const name = trimmed.slice(colon + 1);
  if (set === 'tabler' || set === 'lucide') return { set, name };
  return { set: 'lucide', name: trimmed };
}

interface CmsIconProps {
  /** Icon name from CMS — e.g. "wifi", "lucide:coffee", "tabler:pool" */
  name: string | null | undefined;
  /** Fallback Lucide icon if lookup fails. Defaults to Sparkles. */
  fallback?: LucideIcon;
  size?: number;
  color?: string;
  strokeWidth?: number;
  style?: React.CSSProperties;
  className?: string;
}

export function CmsIcon({
  name,
  fallback = Sparkles,
  size,
  color,
  strokeWidth = 1.5,
  style,
  className,
}: CmsIconProps) {
  const { set, name: n } = parse(name);

  if (set === 'tabler') {
    const Icon = TABLER_MAP[n];
    if (Icon) return <Icon size={size} color={color} stroke={strokeWidth} style={style} className={className} aria-hidden="true" />;
  }

  const Icon: LucideIcon = LUCIDE_MAP[n] ?? fallback;
  return <Icon size={size} color={color} strokeWidth={strokeWidth} style={style} className={className} aria-hidden="true" />;
}
